import React, { useCallback, useEffect, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import { toAuditEntries } from '../adapters';
import type { AuditEntry } from '../../components';

import { REWARD_TYPE_NOTES, REWARD_UNSUPPORTED_FIELDS, toCrmRewardPatch, toUiReward } from '../adapters';
import { RewardCatalogScreen } from '../../screens/RewardCatalogScreen';
import type { Reward } from '../../screens/loyaltyData';

/**
 * ต่อแคตตาล็อกของรางวัลเข้ากับ CRM module (FR-ADM-22)
 *
 * CRM เก็บ ประเภท · คะแนน · ช่วงเวลา · เงื่อนไขการใช้ · การเปิด/ปิด · มูลค่าและอายุคูปองได้แล้ว
 * (คูปองสร้างเป็น VoucherTemplate ของ BIL ให้อัตโนมัติ ไม่ต้องเรียก BIL จากที่นี่)
 *
 * ⚠️ ที่ยังไม่รองรับอยู่ใน REWARD_UNSUPPORTED_FIELDS — หน้าจอซ่อนส่วนเหล่านั้นให้เอง
 *    เพราะช่องที่กรอกได้แต่บันทึกไม่ขึ้นคือกับดักที่ร้ายกว่าการไม่มีช่อง
 * ⚠️ ประเภทเปลี่ยนไม่ได้หลังสร้าง (backend ตอบ 400) จึงส่ง lockTypeOnEdit
 * ⚠️ ปุ่มลบ **ลบจริง** — การหยุดให้แลกชั่วคราวใช้ช่อง "เปิดให้แลกในแอป" (is_active)
 *
 * ⚠️ **การเขียนต้องเป็นสมาชิกของ division**
 *    ถ้าบัญชีไม่ได้เป็นสมาชิกของ division จะอ่านได้แต่บันทึกไม่ได้ ตอบ 403
 *    ข้อความจาก backend ถูกส่งต่อไปแสดงใน toast ตรง ๆ ไม่กลืนทิ้ง
 */
export function RewardCatalogContainer() {
  const { api, dataMode, divisionId } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [rewards, setRewards] = useState<Reward[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /** ประวัติการแก้ของรางวัล — ของเสริม ล้มแล้วหน้ายังใช้งานได้ */
  const [auditEntries, setAuditEntries] = useState<AuditEntry[] | undefined>(undefined);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');
    Promise.all([
      api.listRewards(),
      /* ประวัติเป็นของเสริม ล้มแล้วไม่ควรทำให้แคตตาล็อกใช้ไม่ได้ */
      api.listAuditLogs('reward').catch(() => null),
    ])
      .then(([page, audit]) => {
        if (cancelled) return;
        setRewards(page.items.map(toUiReward));
        setAuditEntries(audit ? toAuditEntries(audit.items) : undefined);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  /**
   * บันทึกของรางวัลหนึ่งรายการ
   *
   * id ของรายการใหม่ที่หน้าจอสร้างเป็น `rw-<timestamp>` ซึ่งไม่ใช่เลข
   * ใช้ตรงนี้แยกว่าเป็นการสร้างใหม่หรือแก้ของเดิม
   */
  const onSave = useCallback(async (r: Reward) => {
    const id = Number(r.id);
    const isNew = !(Number.isInteger(id) && id > 0);
    const patch = toCrmRewardPatch(r, isNew);

    /**
     * รูปเดิมถูกเอาออก — ล้างด้วย PATCH ปกติ ไม่ต้องใช้ multipart
     * ดูจากรายการที่โหลดมา ไม่ใช่จากค่าที่หน้าจอส่งมา เพราะหน้าจอไม่รู้ว่าของเดิมมีรูปไหม
     */
    const had = rewards?.find((x) => x.id === r.id)?.imageUrl;
    if (!isNew && had && !r.imageUrl) patch.image = null;

    const saved = isNew
      ? await api.createReward({ ...patch, division: divisionId })
      : await api.updateReward(id, patch);

    /**
     * อัปโหลดรูปหลังบันทึกสำเร็จ เพราะของใหม่เพิ่งมี id ตอนนี้
     *
     * ⚠️ ถ้าอัปโหลดล้มเหลว ของรางวัลถูกบันทึกไปแล้ว — โยน error ต่อให้หน้าจอบอกผู้ใช้
     *    แล้วโหลดรายการใหม่ก่อน เพื่อไม่ให้ตารางค้างสถานะเก่า
     */
    if (r.imageFile) {
      try {
        await api.uploadRewardImage(saved.id, r.imageFile);
      } catch (err) {
        const fresh = await api.listRewards();
        setRewards(fresh.items.map(toUiReward));
        throw new Error(
          err instanceof Error
            ? `บันทึกของรางวัลแล้ว แต่อัปโหลดรูปไม่สำเร็จ — ${err.message}`
            : 'บันทึกของรางวัลแล้ว แต่อัปโหลดรูปไม่สำเร็จ',
        );
      }
    }

    const fresh = await api.listRewards();
    setRewards(fresh.items.map(toUiReward));
  }, [api, divisionId, rewards]);

  const onDelete = useCallback(async (r: Reward) => {
    const id = Number(r.id);
    if (!Number.isInteger(id) || id <= 0) return;
    await api.deleteReward(id);
    const fresh = await api.listRewards();
    setRewards(fresh.items.map(toUiReward));
  }, []);

  if (isMock) return <RewardCatalogScreen initialState="ready" />;

  return (
    <RewardCatalogScreen
      initialState={state}
      rewards={rewards ?? []}
      onSave={onSave}
      onDelete={onDelete}
      onRefresh={load}
      refreshing={state === 'loading'}
      unsupportedFields={REWARD_UNSUPPORTED_FIELDS}
      lockTypeOnEdit
      typeNotes={REWARD_TYPE_NOTES}
      auditEntries={auditEntries ?? []}
    />
  );
}
